import {Box, Button, CircularProgress, IconButton} from "@mui/material";
import {useEffect, useState} from "react";
import {FiEdit2, FiTrash2} from "react-icons/fi";
import {useLocation, useNavigate} from "react-router-dom";
import TenantForm from "../components/Tenants/TenantForm";
import TenantInfo from "../components/Tenants/TenantInfo";
import TenantUserRolesGrid from "../components/Tenants/TenantUserRolesGrid";
import TenantProductsGrid from "../components/Tenants/TenantProductsGrid";
import Window from "../components/Window/Window";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import DetailTabsSection from "../components/ui/DetailTabsSection";
import useApiRequest from "../hooks/useApiRequest";
import useConfirmDialog from "../hooks/useConfirmDialog";
import {snackbar} from "../utils/snackbar";
import {useAuthPermissions, ACTIONS, RESOURCES} from "../hooks/useAuthPermissions";

const TenantDetail = () => {
  const {state} = useLocation();
  const navigate = useNavigate();
  const id = state?.id;

  const isNewTenant = !id;

  const {data, loading, read, remove} = useApiRequest({baseUrl: `/tenants`, resource: "tenants"});

  const [isEditing, setIsEditing] = useState(isNewTenant);
  const {can} = useAuthPermissions();
  const {confirmOpen, itemName, openDialog, handleConfirm, handleCancel} = useConfirmDialog(async () => {
    const {success, error} = await remove(id, {silentError: true});
    if (!success) {
      if (error?.code === "FORBIDDEN") {
        snackbar.error("No tienes permisos suficientes para eliminar este tenant.");
      } else {
        snackbar.error(error?.userMessage);
      }
      return;
    }

    snackbar.success("Tenant desactivado correctamente.");
    navigate("/tenants");
  });

  useEffect(() => {
    if (!isNewTenant) {
      (async () => {
        const {success, error} = await read(id, {silentError: true});
        if (!success) {
          if (error?.code === "FORBIDDEN") {
            snackbar.error("No tienes permisos suficientes para ver este tenant.");
          } else {
            snackbar.error(error?.userMessage);
          }
        }
      })();
    }
    // eslint-disable-next-line
  }, [id]);

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleOpenDeleteDialog = () => {
    openDialog(id, data?.name || "");
  };

  return (
    <>
      <Window
        title={isNewTenant ? "Nuevo Tenant" : "Detalles del Tenant"}
        backTo="/tenants"
        topRightButtons={
          <>
            {!isNewTenant && (
              <Box component="span">
                {can(ACTIONS.WRITE, RESOURCES.TENANTS) &&
                  (isEditing ? (
                    <Button variant="contained" color="primary" onClick={handleEditToggle}>
                      Cancelar
                    </Button>
                  ) : (
                    <IconButton aria-label="edit" color="primary" onClick={handleEditToggle}>
                      <FiEdit2 />
                    </IconButton>
                  ))}

                {can(ACTIONS.DELETE, RESOURCES.TENANTS) && data?.status !== "deleted" && (
                  <IconButton aria-label="delete" color="error" onClick={handleOpenDeleteDialog}>
                    <FiTrash2 />
                  </IconButton>
                )}
              </Box>
            )}
          </>
        }
      >
        {loading && !isNewTenant ? (
          <CircularProgress />
        ) : (
          <>
            <Box sx={{mb: 4}}>
              {isEditing ? (
                <TenantForm
                  isNew={isNewTenant}
                  refreshData={() => read(id)}
                  setIsEditing={setIsEditing}
                  initialTenantData={data}
                />
              ) : (
                <TenantInfo tenant={data} />
              )}
            </Box>

            {!isNewTenant && (
              <DetailTabsSection
                tabs={[
                  {
                    label: "Productos",
                    content: (
                      <TenantProductsGrid
                        tenantId={id}
                        tenantNif={data?.nif}
                        canAssign={can(ACTIONS.WRITE, RESOURCES.TENANT_PRODUCTS)}
                        canDelete={can(ACTIONS.DELETE, RESOURCES.TENANT_PRODUCTS)}
                      />
                    )
                  },
                  {
                    label: "Usuarios y Roles",
                    content: (
                      <TenantUserRolesGrid
                        tenantId={id}
                        canAssign={can(ACTIONS.WRITE_ROLES, RESOURCES.TENANT_USERS)}
                        canDelete={can(ACTIONS.DELETE_ROLES, RESOURCES.TENANT_USERS)}
                      />
                    )
                  }
                ]}
              />
            )}
          </>
        )}
      </Window>

      <ConfirmDialog
        open={confirmOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        entityName="tenant"
        entityLabel={itemName}
        title="Desactivar "
        confirmText="Desactivar"
        description={
          <>
            ¿Seguro que desea desactivar el tenant
            {itemName && (
              <>
                {" "}
                <strong>{itemName}</strong>
              </>
            )}
            ? <br />
            El tenant no se eliminará, pero quedará <strong>desactivado</strong> y no estará disponible.
          </>
        }
      />
    </>
  );
};

export default TenantDetail;
